import { Injectable } from '@angular/core';
import { CardSet, Pagination, Query } from '@deckplan.io/common';
import { UrlUtils } from '../common/utils/url-utils';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Mockable } from '../mocks/mockable.decorator';

export interface CardSetsAndTotalCount {
    totalCount: number;
    cardSets: CardSet[];
}

@Injectable({
    providedIn: 'root'
})
@Mockable
export class CardSetService {

    private readonly backendUrl = UrlUtils.constructBackendUrl('card-sets');

    private readonly setCache = new Map();
    private readonly setCodeToSetId = new Map();

    constructor(private readonly http: HttpClient) {
        // Set up a cache for us to retrieve individual sets with; since we use them frequently, querying them individually
        // would be very expensive. We'll also set up a map of code to set ID, to provide another lookup mechanism (since cards
        // have the code to refer to the set with).
        this.getAll().subscribe((cardSetsAndTotal) => {
            for (const cardSet of cardSetsAndTotal.cardSets) {
                this.setCache.set(cardSet.id, cardSet);
                this.setCodeToSetId.set(cardSet.code, cardSet.id);
            }
        });
    }

    public getAll(pagination: Partial<Pagination> = null, query: Query = null): Observable<CardSetsAndTotalCount> {
        const url = UrlUtils.constructPaginationAndFilterUrl(this.backendUrl, pagination, query);

        return this.http.get(url, {observe: 'response'})
            .pipe(
                map(res => ({totalCount: parseInt(res.headers.get('total-count'), 10), cardSets: res.body as any}))
            );
    }

    public getOne(setId: string): Observable<CardSet> {
        return of(this.setCache.get(setId) as CardSet);
    }

    public getOneByCode(code: string): Observable<CardSet> {
        return of(this.setCache.get(this.setCodeToSetId.get(code)) as CardSet);
    }
}
